import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Users,
  Video,
  Globe,
  DollarSign,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

const CFI_LOGO = "/images/cfi-logo-main.png";

const ResourceCard = ({ icon: Icon, title, description, buttonText, href, color }) => (
  <Card className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-[#84c77f]">
    <CardHeader>
      <div className={`w-14 h-14 ${color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
        <Icon className="h-7 w-7 text-white" />
      </div>
      <CardTitle className="text-xl">{title}</CardTitle>
      <CardDescription className="text-slate-600">{description}</CardDescription>
    </CardHeader>
    <CardContent>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full"
      >
        <Button className="w-full bg-[#357c2a] hover:bg-[#2a6321] text-white group-hover:bg-[#84c77f] group-hover:text-[#1b4541] transition-colors">
          {buttonText}
          <ExternalLink className="h-4 w-4 ml-2" />
        </Button>
      </a>
    </CardContent>
  </Card>
);

const CFIResources = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Determine back path based on user role
  const getBackPath = () => {
    if (!user) return "/cfi";
    if (user.role === "CLIENT") return "/client/dashboard";
    if (user.role === "BETENBOUGH_ADMIN" || user.role === "CREDITFIRMINC_ADMIN") return "/admin/dashboard";
    return "/team/dashboard";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#265324] to-[#1a3d18] text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={CFI_LOGO} alt="Credit Firm Inc" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold">Credit Firm Resources</h1>
                <p className="text-white/70 text-sm">Tools and resources to help you succeed</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => navigate(getBackPath())}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Portal
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#84c77f]/20 text-[#357c2a] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Partner Resources
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Access exclusive tools, training, and resources to grow your partnership with Credit Firm Inc.
          </p>
        </div>

        {/* Resource Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <ResourceCard
            icon={Users}
            title="Become an Affiliate"
            description="Join our affiliate program and earn commissions by referring clients to Credit Firm Inc. Get your unique referral link today."
            buttonText="Get My Affiliate Link"
            href="https://creditfirminc.net/get_my_link"
            color="bg-gradient-to-br from-[#357c2a] to-[#2d6a5d]"
          />

          <ResourceCard
            icon={Video}
            title="Pro Tips & Training"
            description="Watch our YouTube channel for expert tips, training videos, and strategies to help your clients become loan-ready faster."
            buttonText="Watch Pro Tips"
            href="https://www.youtube.com/channel/UCsTTjHzC8VnFPbX6FJmdtPw"
            color="bg-gradient-to-br from-red-500 to-red-600"
          />

          <ResourceCard
            icon={Globe}
            title="Credit Firm Website"
            description="Visit our main website to learn more about Credit Firm Inc, our services, locations, and how we help clients achieve their homeownership dreams."
            buttonText="Visit Website"
            href="https://creditfirminc.com"
            color="bg-gradient-to-br from-blue-500 to-blue-600"
          />

          <ResourceCard
            icon={DollarSign}
            title="Affiliate Dashboard"
            description="Track your commissions, view your referral history, and manage your affiliate account. Sign in to your affiliate marketing dashboard."
            buttonText="Affiliate Sign In"
            href="https://creditfirminc.app.clientclub.net"
            color="bg-gradient-to-br from-amber-500 to-amber-600"
          />
        </div>

        {/* Additional Info */}
        <Card className="bg-gradient-to-r from-[#265324] to-[#1a3d18] text-white border-0">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">Need Help?</h3>
                <p className="text-white/80">
                  Contact our partner support team for assistance with your account or any questions.
                </p>
              </div>
              <div className="flex gap-3">
                <a href="tel:432-599-4880">
                  <Button variant="secondary" className="bg-white text-[#357c2a] hover:bg-[#84c77f]">
                    Call (432) 599-4880
                  </Button>
                </a>
                <a href="https://www.facebook.com/creditfirmincorporated" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10">
                    Message Us
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-slate-600 text-sm">
          <p>© {new Date().getFullYear()} Credit Firm Inc. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-3">
            <Link to="/cfi" className="hover:text-[#357c2a] transition-colors">
              CFI Landing Page
            </Link>
            <span>•</span>
            <a href="https://creditfirminc.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#357c2a] transition-colors">
              Main Website
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CFIResources;
